import { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { formatKr, fmtShortDate } from "../../lib/format";
import { duration, rateCls } from "./util";

const COLS = [
  ["title", "Tilbud", (p) => p.offer.title.toLowerCase(), false],
  ["date", "Dato", (p) => p.offer.pickup_start, false],
  ["stock", "Lager", (p) => p.offer.stock_total, true],
  ["sell", "Solgt %", (p) => p.sellThrough, true],
  ["picked", "Hentet", (p) => p.picked, true],
  ["noshow", "Udeblevet", (p) => p.noShow, true],
  ["tts", "Udsolgt efter", (p) => p.tts, true],
  ["revenue", "Omsætning", (p) => p.revenue, true],
  ["rate", "Afhentning %", (p) => p.rate, true],
];

const Rate = ({ v }) => (v == null ? <span className="muted">—</span> : <span className={`st-rate ${rateCls(v)}`}>{v} %</span>);

function sortRows(rows, key, dir) {
  const get = COLS.find((c) => c[0] === key)[2];
  return [...rows].sort((a, b) => {
    const x = get(a), y = get(b);
    if (x == null && y == null) return 0;
    if (x == null) return 1;
    if (y == null) return -1;
    return (x < y ? -1 : x > y ? 1 : 0) * (dir === "asc" ? 1 : -1);
  });
}

export default function StatsOffers({ per, saved }) {
  const [sort, setSort] = useState({ key: "date", dir: "desc" });
  const [open, setOpen] = useState({});
  const rows = useMemo(() => sortRows(per, sort.key, sort.dir), [per, sort]);
  const click = (k) => setSort((s) => (s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: k === "title" ? "asc" : "desc" }));
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));

  return (
    <section className="panel" data-testid="stats-offers">
      <div className="st-panel-head">
        <h2>Tilbud <small className="muted">({per.length})</small></h2>
        <label className="st-sort-mobile">
          Sortér
          <select value={`${sort.key}:${sort.dir}`} onChange={(e) => { const [key, dir] = e.target.value.split(":"); setSort({ key, dir }); }} data-testid="stats-offers-sort-select">
            {COLS.flatMap(([k, l]) => [
              <option key={k + "d"} value={`${k}:desc`}>{l} ↓</option>,
              <option key={k + "a"} value={`${k}:asc`}>{l} ↑</option>,
            ])}
          </select>
        </label>
      </div>
      {!per.length ? (
        <p className="st-empty small" data-testid="stats-offers-empty">Ingen tilbud i perioden</p>
      ) : (
        <>
          <div className="st-table-wrap">
            <table className="st-table" data-testid="stats-offers-table">
              <thead>
                <tr>
                  {COLS.map(([k, l, , num]) => {
                    const on = sort.key === k;
                    const Icon = !on ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
                    return (
                      <th key={k} className={num ? "num" : ""} aria-sort={on ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
                        <button type="button" className={`st-th${on ? " on" : ""}`} onClick={() => click(k)} data-testid={`stats-sort-${k}`}>
                          {l} <Icon size={12} aria-hidden="true" />
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <Fragment key={p.offer.id}>
                    <tr data-testid={`stats-offer-row-${p.offer.id}`}>
                      <td className="st-title">
                        {p.variants.length > 0 && (
                          <button type="button" className="st-exp" onClick={() => toggle(p.offer.id)} aria-expanded={!!open[p.offer.id]}
                            aria-label="Vis varianter" data-testid={`stats-offer-expand-${p.offer.id}`}>
                            {open[p.offer.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </button>
                        )}
                        <Link to={`/offers/${p.offer.id}`}>{p.offer.title}</Link>
                      </td>
                      <td>{fmtShortDate(p.offer.pickup_start)}</td>
                      <td className="num">{p.offer.stock_total}</td>
                      <td className="num">{p.sold} <small className="muted">({p.sellThrough} %)</small></td>
                      <td className="num">{p.picked}</td>
                      <td className="num">{p.noShow || <span className="muted">0</span>}</td>
                      <td className="num">{duration(p.tts)}</td>
                      <td className="num"><b>{formatKr(p.revenue)}</b></td>
                      <td className="num"><Rate v={p.rate} /></td>
                    </tr>
                    {open[p.offer.id] && p.variants.map((v) => (
                      <tr key={v.id} className="st-vrow" data-testid={`stats-variant-subrow-${p.offer.id}-${v.id}`}>
                        <td className="st-title">↳ {v.name}</td>
                        <td />
                        <td className="num">{v.stock}</td>
                        <td className="num">{v.sold} <small className="muted">({v.share} % af tilbud)</small></td>
                        <td className="num">{v.picked}</td>
                        <td className="num">{v.noShow || <span className="muted">0</span>}</td>
                        <td />
                        <td className="num">{formatKr(v.revenue)}</td>
                        <td className="num"><Rate v={v.rate} /></td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <ul className="st-cards" data-testid="stats-offers-cards">
            {rows.map((p) => (
              <li key={p.offer.id} className="st-card" data-testid={`stats-offer-card-${p.offer.id}`}>
                <div className="st-card-head">
                  <Link to={`/offers/${p.offer.id}`}>{p.offer.title}</Link>
                  <span className="muted">{fmtShortDate(p.offer.pickup_start)}</span>
                </div>
                <dl>
                  <div><dt>Omsætning</dt><dd>{formatKr(p.revenue)}</dd></div>
                  <div><dt>Solgt</dt><dd>{p.sold}/{p.offer.stock_total} <small>({p.sellThrough} %)</small></dd></div>
                  <div><dt>Afhentning</dt><dd><Rate v={p.rate} /></dd></div>
                  <div><dt>Hentet</dt><dd>{p.picked}</dd></div>
                  <div><dt>Udeblevet</dt><dd>{p.noShow}</dd></div>
                  <div><dt>Udsolgt efter</dt><dd>{duration(p.tts)}</dd></div>
                </dl>
                {p.variants.length > 0 && (
                  <>
                    <button type="button" className="st-card-exp" onClick={() => toggle(p.offer.id)} data-testid={`stats-offer-card-expand-${p.offer.id}`}>
                      {open[p.offer.id] ? "Skjul varianter" : `Vis ${p.variants.length} varianter`}
                    </button>
                    {open[p.offer.id] && (
                      <ul className="st-card-vars">
                        {p.variants.map((v) => (
                          <li key={v.id}><b>{v.name}</b><span>{v.sold} solgt · {v.picked} hentet · {formatKr(v.revenue)}</span></li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      {saved > 0 && (
        <p className="st-saved" data-testid="stats-saved-note">Kunderne har sparet <b>{formatKr(saved)}</b> i forhold til normalpris.</p>
      )}
    </section>
  );
}
